import React, { useEffect, useState } from 'react';
import { Crown, Star, Gift, AlertTriangle, Trophy, ChevronRight, Lock, Check, TrendingUp, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatTL, monthLabel, TIER_COLORS } from '@/lib/format';
import { Card, SectionTitle, SectionLabel, Pill, Progress, Empty } from '@/components/shared';

export default function Rewards() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/architect/rewards').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-ink/40 text-sm">Yükleniyor...</div>;
  if (!data) return <Empty text="Veri yok" />;

  const s = data.summary || {};
  const tiers = [...data.tiers].sort((a, b) => a.threshold - b.threshold);
  const curIdx = tiers.findIndex((t) => t.name === data.currentTier);
  const nextObj = curIdx >= 0 && curIdx < tiers.length - 1 ? tiers[curIdx + 1] : null;
  const curColor = TIER_COLORS[data.currentTier];
  const progress = nextObj && s.nextTierThreshold ? Math.min(100, (s.turnoverMonth / s.nextTierThreshold) * 100) : 100;
  const pct = (r) => `%${(r * 100).toFixed(0)}`;
  const rangeLabel = (i) => {
    const min = tiers[i].threshold;
    const max = i < tiers.length - 1 ? tiers[i + 1].threshold : null;
    return max ? `${formatTL(min)} – ${formatTL(max)} / ay` : `${formatTL(min)}+ / ay`;
  };

  return (
    <div className="px-5 py-6 fade-up" data-testid="rewards-page">
      <h1 className="font-serif text-2xl text-ink mb-5">Ödüller & Seviye</h1>

      {/* CURRENT STATUS */}
      <Card className="p-6 mb-4" data-testid="current-status"
        style={{ backgroundColor: (curColor || '#A9793D') + '10', borderColor: (curColor || '#A9793D') + '55' }}>
        <p className="text-ink-soft text-sm">Şu an</p>
        <div className="flex items-center gap-2 mt-1">
          <Crown strokeWidth={1.5} className="w-6 h-6" style={{ color: curColor }} />
          <span className="font-serif text-3xl" style={{ color: curColor }}>{data.currentTier}</span>
          <span className="font-serif text-3xl text-ink">'dasın</span>
          {data.vip && <Pill tone="gold">VIP</Pill>}
        </div>
        <div className="flex gap-6 mt-4">
          <div>
            <p className="font-serif text-2xl text-ink tabular">{pct(s.tierRate || 0)}</p>
            <p className="text-xs text-ink-soft mt-0.5">komisyon oranı</p>
          </div>
          <div className="border-l border-line pl-6">
            <p className="font-serif text-2xl text-ink tabular">{formatTL(s.turnoverMonth || 0)}</p>
            <p className="text-xs text-ink-soft mt-0.5">bu ayki cironuz</p>
          </div>
        </div>
      </Card>

      {/* NEXT-TIER FOCUS */}
      <Card className="p-6 mb-5 border-gold/50 bg-gold/[0.06]" data-testid="next-tier-focus">
        {nextObj ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp strokeWidth={1.5} className="w-5 h-5 text-gold" />
              <span className="text-sm font-semibold text-ink">Sıradaki hedef: {nextObj.name}</span>
            </div>
            <Progress value={progress} />
            <p className="text-sm text-ink mt-3">
              <span className="font-semibold" style={{ color: TIER_COLORS[nextObj.name] }}>{nextObj.name}</span>
              'e <span className="text-gold font-semibold">{formatTL(s.remainingToNext || 0)}</span> kaldı.
            </p>
            <p className="text-sm text-ink-soft mt-2 leading-relaxed">
              {nextObj.name}'e çıkınca her satıştan <span className="text-ink font-semibold">{pct(nextObj.rate)}</span> kazanırsın.
            </p>
          </>
        ) : (
          <div className="flex items-start gap-3">
            <Crown strokeWidth={1.5} className="w-6 h-6 text-gold shrink-0" />
            <div>
              <p className="font-serif text-xl text-ink">En üst seviyedesin 👑</p>
              <p className="text-sm text-ink-soft mt-1">Zirveyi koru — her satıştan {pct(s.tierRate || 0)} kazanmaya devam et.</p>
            </div>
          </div>
        )}
      </Card>

      {data.protection && (
        <Card className="flex items-start gap-3 border-gold/40 p-4 mb-5" data-testid="rewards-protection">
          <AlertTriangle strokeWidth={1.5} className="w-5 h-5 text-gold shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-ink font-medium">Seviyeni korumak için {formatTL(data.protection.remaining)} kaldı</p>
            <p className="text-xs text-ink-soft mt-0.5">{data.protection.tier} seviyen 1 ay boyunca korunuyor.</p>
          </div>
        </Card>
      )}

      {/* TIER JOURNEY */}
      <SectionLabel>Seviye Yolculuğu</SectionLabel>
      <div className="space-y-3 mb-6" data-testid="tier-journey">
        {tiers.map((t, i) => {
          const color = TIER_COLORS[t.name];
          const active = i === curIdx;
          const unlocked = i <= curIdx;
          const locked = i > curIdx;
          return (
            <Card key={t.name} className={`p-5 relative ${locked ? 'opacity-90' : ''}`} data-testid={`journey-${t.name.toLowerCase()}`}
              style={active ? { backgroundColor: color + '12', borderColor: color + '66' } : {}}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: color + '1f' }}>
                    {locked
                      ? <Lock strokeWidth={1.5} className="w-4 h-4" style={{ color }} />
                      : <Check strokeWidth={2} className="w-4 h-4" style={{ color }} />}
                  </span>
                  <div>
                    <p className="font-serif text-lg leading-none" style={{ color }}>{t.name}</p>
                    <p className="text-[11px] text-ink-soft mt-1">{rangeLabel(i)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="font-serif text-2xl tabular" style={{ color }}>{pct(t.rate)}</span>
                  {active && <div className="mt-1"><Pill tone="gold">Aktifsin</Pill></div>}
                  {locked && <p className="text-[10px] text-ink-faint mt-1">kilidi açılır</p>}
                </div>
              </div>
              <div className="border-t border-line pt-3">
                {i > 0 && <p className="text-[11px] text-ink-faint mb-1.5">Alt seviyelerin tüm ayrıcalıkları +</p>}
                <ul className="space-y-1.5">
                  {(t.perks || []).map((perk, pi) => (
                    <li key={pi} className="flex items-center gap-2 text-[13px] text-ink">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      {perk}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          );
        })}
      </div>

      {/* WHY LEVEL UP */}
      <div className="p-5 mb-6 flex items-start gap-3 rounded-2xl text-ground" data-testid="why-levelup"
        style={{ backgroundColor: 'rgb(var(--ink))' }}>
        <Sparkles strokeWidth={1.5} className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold">Neden yükselmeliyim?</p>
          <p className="text-[13px] text-ground/75 mt-1 leading-relaxed">
            Her seviye = daha yüksek komisyon + daha fazla ayrıcalık. Sattıkça oranın artar,
            ödemende öncelik kazanır ve Bella Maison'ın özel etkinlik ve tanıtım fırsatlarına erişirsin.
          </p>
        </div>
      </div>

      <button onClick={() => navigate('/app/siralama')} data-testid="rewards-goto-leaderboard"
        className="w-full flex items-center justify-between bg-surface-2 rounded-2xl p-4 mb-6 active:scale-[0.98] transition-transform">
        <span className="flex items-center gap-2.5">
          <Trophy strokeWidth={1.5} className="w-5 h-5 text-gold" />
          <span className="text-sm font-semibold text-ink">Mimar sıralamasını gör</span>
        </span>
        <ChevronRight strokeWidth={1.5} className="w-4 h-4 text-ink-faint" />
      </button>

      {/* Ayın Mimarı */}
      {data.ayinMimari && (
        <>
          <SectionTitle>Ayın Mimarı</SectionTitle>
          <Card className="p-5 mb-6 flex items-center gap-4" data-testid="ayin-mimari">
            <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center shrink-0">
              <Star strokeWidth={1.5} className="w-6 h-6 text-gold" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-gold text-xs mb-1">
                <Star strokeWidth={1.25} className="w-3.5 h-3.5" /> {monthLabel(data.ayinMimari.month)}
              </div>
              <p className="font-serif text-lg text-ink">{data.ayinMimari.architect?.name}</p>
              <p className="text-xs text-ink-soft">{data.ayinMimari.architect?.city}</p>
            </div>
          </Card>
        </>
      )}

      {/* Year-end reward */}
      <Card className="p-5 mb-6 flex items-start gap-3">
        <Gift strokeWidth={1.25} className="w-5 h-5 text-redx shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-ink">Yıl sonu ödülü</p>
          <p className="text-xs text-ink-soft mt-1 leading-relaxed">{data.yearEndReward}</p>
        </div>
      </Card>

      {/* Tier history */}
      {data.tierHistory.length > 0 && (
        <>
          <SectionTitle>Seviye geçmişi</SectionTitle>
          <div className="space-y-2" data-testid="tier-history">
            {data.tierHistory.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-line py-2">
                <span className="text-ink-soft">{monthLabel(h.month)}</span>
                <span className="text-ink-soft">{formatTL(h.turnover)}</span>
                <span className="font-medium" style={{ color: TIER_COLORS[h.tier] }}>{h.tier}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
